import { UserExperienceFeedbackProps } from './user-experience-feedback-props.interface';
import { CircleCheck, Clock3, MessageCircle, Sparkles } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { UserExperienceFeedbackStatusDto } from 'shared/interfaces/user-experience-feedback/user-experience-feedback-status.dto';
import { userExperienceFeedbackApi } from 'src/api/user-experience-feedback/user-experience-feedback';
import Coin from 'src/assets/images/icons/custom-icons/Coin';
import Button from 'src/components/Button/Button';
import { ButtonSize } from 'src/components/Button/button-size.enum';
import { ButtonState } from 'src/components/Button/button-state.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import Input from 'src/components/Input/Input';
import { InputState } from 'src/components/Input/input-state.enum';
import { InputType } from 'src/components/Input/input-type.enum';
import Modal from 'src/components/Modal/Modal';
import {
  getStickyFooterModalBodyClassName,
  getStickyFooterModalClassName,
} from 'src/components/Modal/modal-classes.util';
import { ToastType } from 'src/components/Toast/toast-type.enum';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { showToast } from 'src/utils/toast-utils';

const MINIMUM_ANSWER_LENGTH = 10;
const MAXIMUM_ANSWER_LENGTH = 2000;

const UserExperienceFeedback: React.FC<UserExperienceFeedbackProps> = ({ lifetimePoints, onRewardAwarded }) => {
  const t = useTranslationByKey('COMPONENTS.USER_EXPERIENCE_FEEDBACK');
  const { formatPoints } = useUserCurrency();
  const { isMobile } = useBreakpoint();
  const [status, setStatus] = useState<UserExperienceFeedbackStatusDto | null>(null);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loadStartedRef = useRef(false);
  const onRewardAwardedRef = useRef(onRewardAwarded);
  const isEligible = lifetimePoints >= 100;
  const isAnswerValid = answer.trim().length >= MINIMUM_ANSWER_LENGTH;

  useEffect(() => {
    onRewardAwardedRef.current = onRewardAwarded;
  }, [onRewardAwarded]);

  useEffect(() => {
    if (!isEligible || loadStartedRef.current) {
      return;
    }

    loadStartedRef.current = true;
    let isMounted = true;

    const loadStatus = async () => {
      try {
        const nextStatus = await userExperienceFeedbackApi.getStatus();
        if (!isMounted) {
          return;
        }

        setStatus(nextStatus);
        if (nextStatus.result === 'approved') {
          await onRewardAwardedRef.current();
        }
      } catch {
        // Keep the optional dashboard prompt out of the way if its status cannot be loaded.
      }
    };

    void loadStatus();

    return () => {
      isMounted = false;
    };
  }, [isEligible]);

  const handleSubmit = async () => {
    if (!isAnswerValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const nextStatus = await userExperienceFeedbackApi.submit(answer.trim());
      setStatus(nextStatus);
      setAnswer('');
      setIsPromptOpen(false);
      showToast(t('SUBMISSION_SUCCESS'), ToastType.Success);
    } catch {
      showToast(t('SUBMISSION_ERROR'), ToastType.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultClose = () => {
    setStatus((currentStatus) => (currentStatus ? { ...currentStatus, result: null } : currentStatus));
    void userExperienceFeedbackApi.acknowledgeResult();
  };

  return (
    <>
      {isEligible && status?.canSubmit && (
        <div className="relative flex w-full justify-center py-2">
          <div className="absolute inset-x-[8%] inset-y-1 animate-pulse rounded-3xl bg-gradient-to-r from-violet-500 via-fuchsia-400 to-amber-300 opacity-70 blur-xl md:inset-x-[30%]" />
          <Button
            label={t('EARN_POINTS_BUTTON', { points: formatPoints(status.rewardPoints) })}
            iconBefore={<Sparkles className="h-5 w-5" />}
            onClick={() => setIsPromptOpen(true)}
            state={ButtonState.Enabled}
            type={ButtonType.Filled}
            size={ButtonSize.Big}
            className="relative !h-12 w-full max-w-md !rounded-2xl !border-0 !bg-gradient-to-r !from-violet-600 !via-fuchsia-500 !to-amber-400 !font-bold !text-white !shadow-[0_0_28px_rgba(168,85,247,0.62)] hover:!shadow-[0_0_38px_rgba(217,70,239,0.76)]"
          />
        </div>
      )}

      <Modal
        isOpen={isPromptOpen}
        onClose={() => setIsPromptOpen(false)}
        className={getStickyFooterModalClassName(
          isMobile ? '!w-full box-content !rounded-[30px]' : '!max-w-[520px] box-content !rounded-[30px]'
        )}
        modalBodyClassName={getStickyFooterModalBodyClassName(
          'md:!w-[520px] !items-stretch !p-0 overflow-x-hidden overflow-y-auto'
        )}
      >
        <div className="flex w-full flex-col gap-4 p-4 pt-14 md:gap-5 md:p-8 md:pt-14">
          <div className="flex w-full flex-col items-center gap-3 text-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 md:h-12 md:w-12">
              <MessageCircle className="h-5 w-5 md:h-6 md:w-6" />
            </span>
            <div className="flex w-full max-w-full flex-col items-center gap-1.5 px-1">
              <h2 className="w-full break-words text-[1.35rem] font-zillaSlab font-bold leading-tight text-slate-900 md:text-2xl">
                {t('PROMPT_TITLE')}
              </h2>
              <div className="h-1 w-12 rounded-full bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400" />
            </div>
            <p className="max-w-full px-1 text-sm leading-5 text-slate-600 md:leading-6">{t('PROMPT_QUESTION')}</p>
          </div>

          <div className="flex flex-col gap-2">
            <Input
              id="rewardbloom-experience-feedback"
              name="rewardbloom-experience-feedback"
              state={InputState.Enabled}
              inputType={InputType.TextArea}
              value={answer}
              onChange={setAnswer}
              maximumLength={MAXIMUM_ANSWER_LENGTH}
              placeholder={t('ANSWER_PLACEHOLDER')}
              hideClear
              inputClassName="!h-28 !items-start md:!h-40"
            />
            <p className="text-right text-xs text-slate-400">
              {t('CHARACTER_COUNT', {
                count: answer.length,
                min: MINIMUM_ANSWER_LENGTH,
                max: MAXIMUM_ANSWER_LENGTH,
              })}
            </p>
            <p className="text-xs leading-5 text-slate-500">{t('ANSWER_GUIDANCE')}</p>
          </div>

          <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <Clock3 className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="text-sm leading-5">
              {t('MANUAL_APPROVAL_NOTE', { points: formatPoints(status?.rewardPoints ?? 10) })}
            </p>
          </div>

          <Button
            label={t('SUBMIT_BUTTON')}
            onClick={handleSubmit}
            state={isAnswerValid ? ButtonState.Enabled : ButtonState.Disabled}
            type={ButtonType.Filled}
            size={ButtonSize.Big}
            isLoading={isSubmitting}
            className="w-full !bg-violet-600 !text-white hover:!bg-violet-700"
          />
        </div>
      </Modal>

      <Modal
        isOpen={status?.result != null}
        onClose={handleResultClose}
        className={getStickyFooterModalClassName(
          isMobile ? '!w-full box-content !rounded-[30px]' : '!max-w-[460px] box-content !rounded-[30px]'
        )}
        modalBodyClassName={getStickyFooterModalBodyClassName('md:!w-[460px] !items-stretch !p-0 overflow-x-hidden')}
      >
        <div className="flex w-full flex-col items-center gap-4 p-4 pt-14 text-center md:gap-5 md:p-8 md:pt-14">
          <span
            className={
              status?.result === 'approved'
                ? 'flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600'
                : 'flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500'
            }
          >
            {status?.result === 'approved' ? (
              <CircleCheck className="h-9 w-9" />
            ) : (
              <MessageCircle className="h-9 w-9" />
            )}
          </span>

          <div className="flex w-full max-w-full flex-col gap-2 px-1">
            <h2 className="w-full break-words text-[1.35rem] font-zillaSlab font-bold leading-tight text-slate-900 md:text-2xl">
              {status?.result === 'approved'
                ? t('APPROVED_TITLE', { points: formatPoints(status.rewardPoints) })
                : t('REJECTED_TITLE')}
            </h2>
            <p className="text-sm leading-5 text-slate-600 md:leading-6">
              {status?.result === 'approved' ? t('APPROVED_MESSAGE') : t('REJECTED_MESSAGE')}
            </p>
          </div>

          {status?.result === 'approved' && (
            <div className="flex items-center gap-2 rounded-2xl bg-amber-50 px-6 py-3 text-xl font-bold text-amber-700">
              <span>+{formatPoints(status.rewardPoints)}</span>
              <Coin className="h-6 w-6" />
            </div>
          )}

          <Button
            label={t('RESULT_BUTTON')}
            onClick={handleResultClose}
            state={ButtonState.Enabled}
            type={ButtonType.Filled}
            size={ButtonSize.Big}
            className="w-full !bg-violet-600 !text-white hover:!bg-violet-700"
          />
        </div>
      </Modal>
    </>
  );
};

export default UserExperienceFeedback;
